/** A file or a recorded note as a card, shared by every line that carries one. */
import { useEffect, useRef, useState } from "react";
import type { Attachment } from "../../shared/types";
import { extOf } from "../assets/library";
import { AttVideo } from "../components/MessageAttachments";
import { Face } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Sheet } from "../components/ui/Sheet";
import { assetUrl } from "../lib/api";
import { clockSeconds } from "../lib/noteStream";

/** A recorded note's waveform: bars of a steady shape, so the same note always looks the same. */
export function NoteBars({ n, seed }: { n: number; seed: number }) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the bars are a fixed drawing
        <i key={i} style={{ height: `${28 + Math.round(Math.abs(Math.sin(i * 1.1 + seed) * 52 + Math.sin(i * 0.37) * 18))}%` }} />
      ))}
    </>
  );
}

const seedOf = (s: string) => [...s].reduce((n, c) => n + c.charCodeAt(0), 0) % 7;

/** Where a file plays or opens from: a note's gated address, else the stored file. */
export function srcOf(a: Attachment): string | null {
  return a.media_url ?? (a.key ? assetUrl(a.key) : (a.url ?? null));
}

function isNote(a: Attachment): boolean {
  return (a.kind === "voice" || a.kind === "video") && !!a.media_key;
}

/** A file in a word, with its length and size: "PDF · 1.2 MB", "Video · 1:04 · 18 MB". */
function attLine(a: Attachment): string {
  const kind = a.kind === "image" ? "Picture" : a.kind === "video" ? "Video" : a.kind === "voice" ? "Sound" : extOf(a.name) || "File";
  return [kind, a.duration, a.size].filter(Boolean).join(" · ");
}

/** The small square of a file. */
export function AttThumb({ a, local }: { a: Attachment; local?: string | null }) {
  const src = local ?? srcOf(a);
  if (a.kind === "image" && src) return <img src={src} alt="" />;
  if (a.kind === "video" && a.poster_url) return <img src={a.poster_url} alt="" />;
  if (a.kind === "video" && local) return <video src={`${local}#t=0.5`} muted playsInline preload="metadata" />;
  const ext = extOf(a.name);
  return (
    <span className={`as-ti ${a.kind === "file" ? (ext === "PDF" ? "pdf" : "doc") : a.kind}`}>
      {a.kind === "file" ? <b>{ext || "TXT"}</b> : <Icon name={a.kind === "voice" ? "music" : a.kind === "video" ? "video" : "pic"} />}
    </span>
  );
}

/** A voice note's play button: the note plays where it sits. */
function VoicePlay({ src }: { src: string | null }) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => () => audio.current?.pause(), []);
  const toggle = () => {
    if (!src) return;
    if (!audio.current) {
      const a = new Audio(src);
      a.addEventListener("ended", () => setPlaying(false));
      a.addEventListener("pause", () => setPlaying(false));
      a.addEventListener("play", () => setPlaying(true));
      audio.current = a;
    }
    if (audio.current.paused) void audio.current.play().catch(() => setPlaying(false));
    else audio.current.pause();
  };
  return (
    <button type="button" className="mnote-p" aria-label={playing ? "Pause" : "Play the voice note"} onClick={toggle} disabled={!src}>
      <Icon name={playing ? "pause" : "asplay"} />
    </button>
  );
}

/** A recorded note as a card: a voice note plays where it sits, a video note opens its player. */
export function NoteCard({ a, local, host, className = "mfile", status }: {
  a: Attachment;
  /** The take's own address while it uploads. */
  local?: string | null;
  host: { name: string; photo?: string | null };
  className?: string;
  /** Instead of its length while it uploads. */
  status?: string;
}) {
  const [open, setOpen] = useState(false);
  const video = a.kind === "video";
  const src = local ?? srcOf(a);
  const length = status ?? (a.duration || "");
  return (
    <div className={`${className} mnote`}>
      {video ? (
        <button type="button" className="mnote-v" aria-label="Play the video note" onClick={() => setOpen(true)} disabled={!src}>
          {a.poster_url ? <img src={a.poster_url} alt="" /> : local ? <video src={`${local}#t=0.5`} muted playsInline preload="metadata" /> : <Face name={host.name} photo={host.photo} size={56} />}
          <i>
            <Icon name="asplay" />
          </i>
        </button>
      ) : (
        <>
          <VoicePlay src={src} />
          <span className="mnote-w">
            <NoteBars n={22} seed={seedOf(a.media_key ?? a.name)} />
          </span>
        </>
      )}
      <span className="sp">
        <b>{video ? "Video note" : "Voice note"}</b>
        <span className="num">{length}</span>
      </span>
      {video && src ? (
        <Sheet open={open} onClose={() => setOpen(false)} title="Video note">
          <div className="sheet-b">
            <AttVideo src={src} poster={a.poster_url ?? undefined} label="Video note" duration={clockSeconds(a.duration)} />
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}

/** A file or picture in the thread: it opens in a new tab. */
function FileCard({ a, className = "mfile" }: { a: Attachment; className?: string }) {
  const src = srcOf(a);
  const inner = (
    <>
      <span className="as-pf-t">
        <AttThumb a={a} />
      </span>
      <span className="sp">
        <b>{a.name}</b>
        <span>{attLine(a)}</span>
      </span>
    </>
  );
  return src ? (
    <a className={className} href={src} target="_blank" rel="noreferrer noopener" download={a.kind === "file" ? a.name : undefined}>
      {inner}
    </a>
  ) : (
    <div className={className}>{inner}</div>
  );
}

export function AttCard({ a, host }: { a: Attachment; host: { name: string; photo?: string | null } }) {
  return isNote(a) ? <NoteCard a={a} host={host} /> : <FileCard a={a} />;
}
